import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';

interface CommissionTier {
  id: string;
  name: string;
  minValue: number | null;
  maxValue: number | null;
  ratePercent: number;
  createdAt: string;
}

interface CommissionPayout {
  id: string;
  opportunityId: string;
  opportunityName: string;
  repId: string;
  repEmail: string | null;
  dealValue: number;
  ratePercent: number;
  payoutAmount: number;
  state: string;
  tierId: string | null;
  tierName: string | null;
  approvalNotes: string | null;
  createdAt: string;
}

const emptyTier = () => ({
  name: '',
  minValue: '',
  maxValue: '',
  ratePercent: '',
});

export function CommissionTiersAdminPage() {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState<CommissionTier[]>([]);
  const [payouts, setPayouts] = useState<CommissionPayout[]>([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [loadingPayouts, setLoadingPayouts] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('ALL');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyTier());
  const [saving, setSaving] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [approveNotes, setApproveNotes] = useState('');
  const [approving, setApproving] = useState(false);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const loadTiers = useCallback(async () => {
    try {
      const { data } = await apiClient.get<CommissionTier[]>('/admin/commission-tiers');
      setTiers(data);
    } catch {
      setTiers([]);
    } finally {
      setLoadingTiers(false);
    }
  }, []);

  const loadPayouts = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (filter !== 'ALL') params.state = filter;
      const { data } = await apiClient.get<CommissionPayout[]>('/admin/commission-payouts', { params });
      setPayouts(data);
    } catch {
      setPayouts([]);
    } finally {
      setLoadingPayouts(false);
    }
  }, [filter]);

  useEffect(() => { loadTiers(); }, [loadTiers]);
  useEffect(() => { loadPayouts(); }, [loadPayouts]);

  function openCreate() {
    setEditId(null);
    setForm(emptyTier());
    setDialogOpen(true);
  }

  function openEdit(tier: CommissionTier) {
    setEditId(tier.id);
    setForm({
      name: tier.name,
      minValue: tier.minValue != null ? String(tier.minValue) : '',
      maxValue: tier.maxValue != null ? String(tier.maxValue) : '',
      ratePercent: String(tier.ratePercent),
    });
    setDialogOpen(true);
  }

  async function save() {
    if (!form.name.trim() || !form.ratePercent) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      minValue: form.minValue ? parseFloat(form.minValue) : null,
      maxValue: form.maxValue ? parseFloat(form.maxValue) : null,
      ratePercent: parseFloat(form.ratePercent),
    };
    try {
      if (editId) {
        await apiClient.put(`/admin/commission-tiers/${editId}`, payload);
      } else {
        await apiClient.post('/admin/commission-tiers', payload);
      }
      showToast(t('commissionTiers.saved'));
      setDialogOpen(false);
      loadTiers();
    } catch {
      showToast(t('commissionTiers.saved') + ' — error');
    } finally {
      setSaving(false);
    }
  }

  async function deleteTier(id: string) {
    if (!window.confirm(t('commissionTiers.delete'))) return;
    try {
      await apiClient.delete(`/admin/commission-tiers/${id}`);
      showToast(t('commissionTiers.deleted'));
      loadTiers();
    } catch {
      showToast(t('commissionTiers.deleted') + ' — error');
    }
  }

  function openApprove(id: string) {
    setApproveId(id);
    setApproveNotes('');
    setApproveOpen(true);
  }

  async function submitApprove() {
    if (!approveId) return;
    setApproving(true);
    try {
      await apiClient.post(`/admin/commission-payouts/${approveId}/approve`, {
        notes: approveNotes.trim() || null,
      });
      showToast(t('commissionPayouts.approved'));
      setApproveOpen(false);
      loadPayouts();
    } catch {
      showToast(t('commissionPayouts.approved') + ' — error');
    } finally {
      setApproving(false);
    }
  }

  async function markPaid(id: string) {
    try {
      await apiClient.post(`/admin/commission-payouts/${id}/paid`);
      showToast(t('commissionPayouts.paid'));
      loadPayouts();
    } catch {
      showToast(t('commissionPayouts.paid') + ' — error');
    }
  }

  const FILTER_OPTIONS = ['ALL', 'PENDING', 'APPROVED', 'PAID'] as const;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <Breadcrumbs crumbs={[{ label: t('commissionTiers.title') }]} />

      {toast && (
        <div className="fixed bottom-4 right-4 bg-foreground text-background px-4 py-2 rounded shadow z-50 text-sm">
          {toast}
        </div>
      )}

      {/* Tiers section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold">{t('commissionTiers.title')}</h1>
            <p className="text-muted-foreground text-sm mt-1">{t('commissionTiers.subtitle')}</p>
          </div>
          <Button onClick={openCreate} className="gap-1">
            <Plus className="h-4 w-4" /> {t('commissionTiers.new_button')}
          </Button>
        </div>

        {loadingTiers ? (
          <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
        ) : tiers.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('commissionTiers.empty')}</p>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2">{t('commissionTiers.col_name')}</th>
                  <th className="text-right px-4 py-2">{t('commissionTiers.col_min')}</th>
                  <th className="text-right px-4 py-2">{t('commissionTiers.col_max')}</th>
                  <th className="text-right px-4 py-2">{t('commissionTiers.col_rate')}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tiers.map((tier) => (
                  <tr key={tier.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">{tier.name}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {tier.minValue != null ? tier.minValue.toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {tier.maxValue != null ? tier.maxValue.toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">{Number(tier.ratePercent).toFixed(1)}%</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(tier)}>
                          {t('common.edit')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteTier(tier.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payouts section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold">{t('commissionPayouts.admin_title')}</h2>
            <p className="text-muted-foreground text-sm mt-1">{t('commissionPayouts.admin_subtitle')}</p>
          </div>
          <div className="flex gap-1">
            {FILTER_OPTIONS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${filter === f ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
              >
                {f === 'ALL' ? t('commissionPayouts.filter_all') : t(`commissionPayouts.filter_${f}`)}
              </button>
            ))}
          </div>
        </div>

        {loadingPayouts ? (
          <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
        ) : payouts.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('commissionPayouts.empty')}</p>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2">{t('commissionPayouts.col_rep')}</th>
                  <th className="text-left px-4 py-2">{t('commissionPayouts.col_deal')}</th>
                  <th className="text-right px-4 py-2">{t('commissionPayouts.col_rate')}</th>
                  <th className="text-right px-4 py-2">{t('commissionPayouts.col_payout')}</th>
                  <th className="text-left px-4 py-2">{t('commissionPayouts.col_tier')}</th>
                  <th className="text-left px-4 py-2">{t('commissionPayouts.col_state')}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payouts.map((p) => (
                  <tr key={p.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.repEmail ?? p.repId.slice(0, 8)}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[160px]">{p.opportunityName}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{Number(p.dealValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right">{Number(p.ratePercent).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-right font-semibold">{Number(p.payoutAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{p.tierName ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.state === 'PAID' ? 'bg-emerald-900/40 text-emerald-200' :
                        p.state === 'APPROVED' ? 'bg-blue-900/40 text-blue-200' :
                        'bg-amber-900/40 text-amber-200'
                      }`}>
                        {t(`commissionPayouts.status_${p.state}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {p.state === 'PENDING' && (
                          <Button size="sm" onClick={() => openApprove(p.id)}>
                            {t('commissionPayouts.approve')}
                          </Button>
                        )}
                        {p.state === 'APPROVED' && (
                          <Button size="sm" variant="outline" onClick={() => markPaid(p.id)}>
                            {t('commissionPayouts.mark_paid')}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tier create/edit dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-card border rounded-xl shadow-xl p-6">
            <h2 className="font-semibold text-lg mb-4">{t('commissionTiers.dialog_title')}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('commissionTiers.field_name')}</label>
                <Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('commissionTiers.field_min')}</label>
                  <Input className="mt-1" type="number" min={0} step="1000" placeholder="0" value={form.minValue} onChange={(e) => setForm({ ...form, minValue: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('commissionTiers.field_max')}</label>
                  <Input className="mt-1" type="number" min={0} step="1000" placeholder="∞" value={form.maxValue} onChange={(e) => setForm({ ...form, maxValue: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">{t('commissionTiers.field_rate')}</label>
                <Input className="mt-1" type="number" min={0} max={100} step="0.1" placeholder="5.0" value={form.ratePercent} onChange={(e) => setForm({ ...form, ratePercent: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={save} disabled={saving || !form.name.trim() || !form.ratePercent}>
                {saving ? t('commissionTiers.saving') : t('commissionTiers.save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approve dialog */}
      {approveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-card border rounded-xl shadow-xl p-6">
            <h2 className="font-semibold text-lg mb-4">{t('commissionPayouts.approve')}</h2>
            <label className="text-sm font-medium">{t('commissionPayouts.notes_label')}</label>
            <textarea
              className="mt-1 w-full border rounded-md p-2 text-sm resize-none h-20 bg-background"
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={approving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submitApprove} disabled={approving}>
                {approving ? '…' : t('commissionPayouts.approve')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
